import { useEffect, useMemo, useState } from 'react';
import { fetchAssignableProviders } from '../services/api/staffApi';

/**
 * useAssignableProviders — the ONE source of the doctor / nurse / lab-tech
 * PICKER lists (admission-access S10). The server scopes the list to what the
 * caller may pick (department wall); the client never re-filters it, and
 * never falls back to the org-wide roster while it loads.
 *
 * Names elsewhere (history, referrals, "who did X") still resolve org-wide
 * from fetchStaffList / visitStore.doctors — this hook is for pickers only.
 *
 * One request per role per session: fetchAssignableProviders is single-flight
 * + cached, so re-renders and sibling pickers never multiply requests. The
 * effect depends on the `role` string only.
 *
 * @param {string|null|undefined} role - a STAFF_ROLES value; falsy = idle (no
 *   request, loading false) so a picker that is not on screen costs nothing.
 * @param {string} [purpose] - an ASSIGNABLE_PURPOSE value (opt-in, e.g. the
 *   org-wide casualty roster); omitted = the scoped default list. A primitive,
 *   so it is as safe in the effect deps as `role`.
 * @returns {{ providers: Array, loading: boolean, error: string|null }}
 *   `providers` keeps the SAME reference until a new result lands.
 */

// Shared empty list — a stable reference for "nothing yet / nothing to show".
const NO_PROVIDERS = [];

// Picker copy (PO-ratified, copy-ratified.md #5/#30/#31). Exported so every
// provider picker (Register Visit, Admit, Book Appointment, desk filter,
// Manage Visits / Appointments, dashboard cards) says the same thing.
export const DOCTORS_LOADING_COPY = 'Loading doctors…';
export const NURSES_LOADING_COPY = 'Loading nurses…';
export const LAB_TECHS_LOADING_COPY = 'Loading lab technicians…';
// Rendered whenever `error` is truthy — never the error's own text (a server
// message is not UI copy).
export const DOCTORS_LOAD_ERROR_COPY = "Couldn't load doctors. Refresh the page to try again.";
export const NURSES_LOAD_ERROR_COPY = "Couldn't load nurses. Refresh the page to try again.";
export const LAB_TECHS_LOAD_ERROR_COPY = "Couldn't load lab technicians. Refresh the page to try again.";
export const DOCTORS_EMPTY_COPY = 'No doctors added yet.';
export const DOCTORS_EMPTY_WALLED_COPY = 'No doctors in your departments yet. Ask your organisation admin to add one.';
// Empty server list: a department-walled viewer reads "your departments", an
// unwalled one has simply no doctors.
export const doctorsEmptyCopy = (viewerIsDeptScoped) =>
  (viewerIsDeptScoped ? DOCTORS_EMPTY_WALLED_COPY : DOCTORS_EMPTY_COPY);
export const DOCTORS_SCOPED_FOOTER_COPY = 'Only doctors from your departments are listed.';

export default function useAssignableProviders(role, purpose) {
  // The last settled fetch, tagged with the role + purpose it answered — so a
  // change of either reads as "loading" again instead of showing the previous list.
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!role) return undefined;
    let cancelled = false;
    // Default call keeps the one-argument shape (`fetchAssignableProviders(role)`).
    (purpose ? fetchAssignableProviders(role, purpose) : fetchAssignableProviders(role)).then((res) => {
      if (cancelled) return;
      setResult({
        role,
        purpose,
        providers: res.success ? res.data : NO_PROVIDERS,
        error: res.success ? null : res.error,
      });
    });
    return () => { cancelled = true; };
  }, [role, purpose]);

  return useMemo(() => {
    if (!role) return { providers: NO_PROVIDERS, loading: false, error: null };
    if (result?.role !== role || result.purpose !== purpose) {
      return { providers: NO_PROVIDERS, loading: true, error: null };
    }
    return { providers: result.providers, loading: false, error: result.error };
  }, [role, purpose, result]);
}
